import React from 'react';
import Immutable from 'immutable';
import {Trans} from '@lingui/react';
import {useSelector, useDispatch} from 'react-redux';
import {DialogContent, DialogOverlay} from '@reach/dialog';
import {push} from 'connected-react-router/immutable';

import {HOME, PRIVACY} from '@services/routes';
import {getFromLocation} from '@services/auth';
import {privacyAgreementLoadingSuccess} from '@actions/privacyAgreement';
import {loadUserCampaignsSuccess, selectUserCampaign} from '@actions/userCampaign';
import {setThemeId} from '@actions/ui';
import {THEMES} from '@services/utilities';
import {i18n} from '@services/i18n';
import {formatDate} from '@services/utilities';

import FlatButton from '@components/FlatButton/FlatButton';
import LogoSkillup from '@components/Icons/SkillupLogo/LogoSkillup';

import cl from 'classnames';
import s from './CampaignSelector.css';

export default function CampaignSelector() {
  const isAssessor = useSelector(
    state => state.getIn(['user', 'data', 'role', 'name']) === 'Assessor'
  );
  const languages = useSelector(state => state.getIn(['languages', 'list']));
  const campaigns = useSelector(state => state.getIn(['userCampaign', 'list']));
  const userCampaign = useSelector(state => state.getIn(['userCampaign', 'data']));
  const fromLocation = useSelector(getFromLocation);

  const [selectedCampaign, setSelectedCampaign] = React.useState(
    campaigns.size === 1 ? campaigns.getIn([0, 'id']) : null
  );
  const [selectedLang, setSelectedLang] = React.useState('');
  const [isSelecting, setIsSelecting] = React.useState(false);

  const dispatch = useDispatch();

  function generateCampaignInfo(is360, autoEvaluated) {
    if (!is360) return null;

    let info = 'Includes 360 questionnaires to evaluate';

    if (autoEvaluated) {
      info = `${info} others`;
    } else {
      info = `${info} self/others`;
    }

    return info;
  }

  function confirmLang(lang, campaignId = selectedCampaign) {
    const campaign = campaigns.find(campaign => campaign.get('id') === campaignId);
    const theme = campaign.get('theme');

    setIsSelecting(true);
    dispatch(selectUserCampaign(campaignId, lang)).then(resp => {
      setIsSelecting(false);

      const {privacy, privacyAgreement, campaign} = resp;
      const userLang = campaign.user.lang;
      const correctLangLabels = campaign.languages.find(label => label.lang === userLang) || {};
      const newCampaign = {...campaign, ...correctLangLabels};
      dispatch(loadUserCampaignsSuccess(Immutable.fromJS(newCampaign)));
      dispatch(isAssessor ? setThemeId(THEMES.BASIC) : setThemeId(theme));

      if (privacyAgreement) {
        dispatch(privacyAgreementLoadingSuccess(privacyAgreement));
      }

      if (!privacy) {
        dispatch(push(PRIVACY));
        return;
      }

      if (fromLocation) {
        dispatch(push(fromLocation !== LOGIN ? fromLocation : HOME));
      }

      dispatch(push(HOME));
    });
  }

  function selectCampaign(campaignId) {
    const campaign = campaigns.find(campaign => campaign.get('id') === campaignId);
    const userLang = campaign.getIn(['user', 'lang']);
    const campaignLanguages = campaign.get('languages');
    const campaignLanguagesSize = campaign.get('languages').size;
    const lang = campaignLanguages.getIn([0, 'lang']);

    if (campaignLanguagesSize > 1 && !userLang) {
      setSelectedCampaign(campaignId);
      return;
    }

    setIsSelecting(true);
    confirmLang(userLang ? null : lang, campaign.get('id'));
  }

  function getCampaignLabelsInLang(labels) {
    return (
      labels.find(lang => lang.get('lang').includes(locale)) ||
      labels.find(lang => lang.get('lang').includes('en')) ||
      labels.getIn([0])
    );
  }

  function getLastAccess(date) {
    if (!date) {
      return '';
    }

    return formatDate(date, 'dd/MM/yyyy');
  }

  function selectLang(langName) {
    setSelectedLang(langName);
  }

  function closeModal(campaigns) {
    if (campaigns.size > 1) {
      setSelectedCampaign(null);
    }
  }

  const locale = i18n._locale;
  const showSelectCampaignModal = Boolean(selectedCampaign);
  const campaign = showSelectCampaignModal
    ? campaigns.find(campaign => campaign.get('id') === selectedCampaign)
    : null;
  const campaignLabels = campaign ? campaign.get('languages') : null;
  const campaignLanguages = campaignLabels
    ? campaignLabels
        .map(label => languages.find(lang => lang.get('name') === label.get('lang')))
        //make sure that default lang comes first
        .sort(a => (a.get('name') === campaign.get('lang') ? -1 : 0))
    : null;

  return (
    <>
      <DialogOverlay
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.6)',
        }}
        isOpen={showSelectCampaignModal}
        onDismiss={() => closeModal(campaigns)}
      >
        <DialogContent className={s.DialogContent}>
          {showSelectCampaignModal && campaignLabels && campaignLabels.size > 1 ? (
            <div className={s.Modal}>
              <div>
                <div className={s.CampaignTitle}>
                  {getCampaignLabelsInLang(campaignLabels).get('name')}
                </div>
                <div>
                  <Trans id="campaign_select.modal_title">
                    Scegli in quale lingua visualizzare i contenuti
                  </Trans>
                </div>
              </div>

              <div>
                <div>
                  <Trans id="campaign_select.modal_subtitle">
                    Seleziona una lingua. Attenzione, la lingua selezionata non potrà essere più
                    cambiata
                  </Trans>
                </div>

                <div className={s.LangsContainer}>
                  {campaignLanguages.map(lang => {
                    return (
                      <button
                        lang={lang.get('name')}
                        onClick={() => selectLang(lang.get('name'))}
                        aria-pressed={selectedLang === lang.get('name') ? 'true' : 'false'}
                        className={
                          selectedLang === lang.get('name') ? 'lang-btn selected' : 'lang-btn'
                        }
                      >
                        {lang.get('description')}
                      </button>
                    );
                  })}
                </div>

                <FlatButton
                  className={s.Confirm}
                  onClick={() => confirmLang(selectedLang)}
                  disabled={!selectedLang}
                >
                  <Trans id="campaign_select.modal_confirm">CONFERMA E INIZIA</Trans>
                </FlatButton>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </DialogOverlay>

      <div className={s.Container}>
        <div className={s.Sidebar}>
          <LogoSkillup />
          <h1>
            <Trans id="campaign_select.welcome">Benvenuto</Trans>
          </h1>
          <div className={s.Divider}></div>
          <h2>
            <Trans id="campaign_select.title">Seleziona una campagna</Trans>
          </h2>
        </div>

        <div className={s.CampaignList}>
          {campaigns &&
            campaigns.size > 1 &&
            campaigns.map(item => {
              const labels =
                item.get('languages').find(lang => locale.includes(lang.get('lang'))) ||
                item.get('languages').find(lang => lang.get('lang').includes('en')) ||
                item.getIn(['languages', 0]);

              const userLastAccess = item.getIn(['user', 'access']);

              return (
                <div class={s.CampaignListItem}>
                  <div className={s.Title}>{labels.get('name')}</div>

                  {userLastAccess ? (
                    <div className={s.CampaignInfo}>
                      Ultimo accesso: {getLastAccess(item.getIn(['user', 'access']))}
                    </div>
                  ) : (
                    <div className={cl(s.CampaignInfo, s.FakeBtn)}>
                      <Trans id="campaign_select.new">Nuova!</Trans>
                    </div>
                  )}

                  <FlatButton onClick={() => selectCampaign(item.get('id'))}>
                    <Trans id="campaign_select.enter">ENTRA</Trans>
                  </FlatButton>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
}
